import { Availability } from '../types/availability';

/**
 * 'gap-after' — this day is an availability's endDate (checkout) and no other period
 * picks up coverage until a later day, leaving at least one uncovered night right after it.
 * 'gap-before' — this day is an availability's startDate (check-in) and the previous period's
 * checkout left at least one uncovered night right before it.
 */
export type AvailabilityGapMarker = 'gap-before' | 'gap-after';
export type AvailabilityGapMap = ReadonlyMap<string, AvailabilityGapMarker>;

const toDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Flags the two boundary days around a genuine coverage gap between consecutive availability
 * periods, so the calendar can visually distinguish a checkout/check-in pair that both render
 * green from one that's actually followed/preceded by an unavailable night.
 *
 * `periods` must be sorted by startDate ASC (same precondition as `buildAvailabilityMap`).
 * A pair is only flagged when the next period's startDate is strictly after the current
 * period's endDate — same-day turnovers (checkout day === check-in day) are not gaps.
 */
export const buildAvailabilityGapMap = (periods: readonly Availability[]): AvailabilityGapMap => {
  const map = new Map<string, AvailabilityGapMarker>();

  for (let i = 0; i < periods.length - 1; i++) {
    const current = periods[i];
    const next = periods[i + 1];

    if (toDate(next.startDate) > toDate(current.endDate)) {
      map.set(current.endDate, 'gap-after');
      map.set(next.startDate, 'gap-before');
    }
  }

  return map;
};
